import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'
import { DeleteOutline, EditOutlined } from '@material-ui/icons'
// Imported directly (not from the '@material-ui/icons' barrel) because this file only
// ever references it as a default parameter value, never as a JSX tag — a usage shape
// babel-plugin-import's per-icon rewrite doesn't recognize, which silently drops the
// barrel import for it while still removing the original import declaration.
import ReceiptOutlined from '@material-ui/icons/ReceiptOutlined'

import { DRAWER_TYPES, strings } from '@constants'
import { toggleDrawer } from '@data/state/action/root'
import { displayAmount } from '@utils'

import Table, { TableActionsWrapper, TableActionButton } from '../table'
import CardHeaderV2 from '../cardHeader'

// Generic display card for a "product line item + rate" charges list, backed by the
// shared AddOtherCharges drawer (DRAWER_TYPES.OTHER_CHARGES_PO). The drawer itself only
// deals in plain arrays, so this component has no opinion on where `charges` lives
// (Immutable record, plain reducer state, etc.) or how `onSave` persists it — callers own
// that entirely.
const OtherChargesCard = ({ charges, onSave, onDelete, icon: Icon = ReceiptOutlined, title, emptyText, drawerLabel, disabled = false }) => {
	const theme = useTheme()
	const globalDispatch = useDispatch()

	const editOtherCharges = useCallback(() => {
		globalDispatch(
			toggleDrawer(true, DRAWER_TYPES.OTHER_CHARGES_PO, {
				label: drawerLabel ?? strings(['edit', 'other', 'charges']),
				edit: true,
				data: charges,
				editCallback: (list) => {
					onSave(list)
					globalDispatch(toggleDrawer(false))
				},
			})
		)
	}, [globalDispatch, drawerLabel, charges, onSave])

	const columns = useMemo(
		() => [
			{ label: strings('charge', 'type'), key: 'product_name', renderer: (val) => val || '-', sortable: false },
			{ label: strings('hsn_code'), key: 'hsn_code', renderer: (val) => val || '-', sortable: false, mobile: false },
			{
				label: strings(['rate', 'per', 'mt']),
				key: 'rate_per_mt',
				renderer: (val) => (val !== null && val !== undefined ? displayAmount(val) : '-'),
				sortable: false,
			},
			{
				label: strings('lumpsum', 'amount'),
				key: 'rate_lumpsum',
				renderer: (val) => (val !== null && val !== undefined ? displayAmount(val) : '-'),
				sortable: false,
			},
			{
				label: strings('actions'),
				align: 'right',
				renderer: (_v, row) => {
					const index = (charges ?? []).indexOf(row)
					return (
						<TableActionsWrapper>
							<TableActionButton
								disabled={disabled}
								color={theme.colors.status.error.text}
								onClick={() => (onDelete ? onDelete(index) : editOtherCharges())}
							>
								<DeleteOutline />
							</TableActionButton>
							<TableActionButton disabled={disabled} color={theme.colors.brand.primary} onClick={editOtherCharges}>
								<EditOutlined />
							</TableActionButton>
						</TableActionsWrapper>
					)
				},
			},
		],
		[theme, charges, onDelete, editOtherCharges, disabled]
	)

	return (
		<>
			<CardHeaderV2
				title={title ?? strings('other', 'charges')}
				icon={Icon}
				showAction
				actionLabel={charges?.length > 0 ? strings('edit') : strings('add')}
				onAction={editOtherCharges}
				disabled={disabled}
			/>
			<Table columns={columns} data={charges ?? []} hideFooter emptyState={emptyText ? { text: emptyText } : undefined} />
		</>
	)
}

export default OtherChargesCard
